import React, { useEffect, useState, useCallback } from "react";
import { MenuItem, ListItemIcon, ListItemText, Menu } from "@mui/material";
import {
  ApartmentOutlined,
  Inventory2Outlined,
  SettingsOutlined,
  MergeTypeOutlined,
  EditNoteOutlined,
  ChevronRight,
} from "@mui/icons-material";
import { fetchUserAttributes } from "@aws-amplify/auth";
import { AdjustInventory } from "./menus/InventoryAdjustment/AdjustInventory";
import InventoryDetailsEditable from "./menus/InventoryDetailsEditable/InventoryDetailsEditable";
import MergeInventory from "./menus/MergeInventory/MergeInventory";
import InventoryBulkEdit from "./menus/InventoryBulkEdit/InventoryBulkEdit";
import FamilyManagement from "./menus/FamilyManagementInventory/FamilyManagement";
import { BaseContextMenu, BaseContextMenuProps } from "../../../../components/datagrid/components/BaseContextMenu";

const InventoryContextMenu: React.FC<BaseContextMenuProps> = ({
  open,
  event,
  closeContextMenu,
  selectedRows,
  onGridRefresh,
  ...rest
}) => {
  const [inventoryAnchorEl, setInventoryAnchorEl] =
    useState<null | HTMLElement>(null);
  const [activeModal, setActiveModal] = useState<string>("");
  const [userId, setUserId] = useState<string>("");

  // Fetch userId on mount
  useEffect(() => {
    const getUserId = async () => {
      try {
        const user = await fetchUserAttributes();
        const id = user.email?.split("@")[0];
        if (id) {
          setUserId(id);
        }
      } catch (error) {
        console.error("Error fetching user attributes:", error);
      }
    };
    getUserId();
  }, []);

  const openContextModal = useCallback((modalName: string) => {
    setActiveModal(modalName);
    setInventoryAnchorEl(null);
  }, []);

  const closeModalAndRefresh = useCallback(() => {
    setActiveModal("");
    if (onGridRefresh) {
      onGridRefresh();
    }
    closeContextMenu();
  }, [onGridRefresh]);

  const handleInventoryMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setInventoryAnchorEl(event.currentTarget);
  };

  const handleInventoryMenuClose = () => {
    setInventoryAnchorEl(null);
  };

  const handleMainMenuClose = () => {
    setInventoryAnchorEl(null);
    setActiveModal("");
    closeContextMenu();
  };

  // Resolve which custom modal to show (or null for none)
  let resolvedCustomModal: React.ReactNode | null = null;
  if (activeModal) {
    switch (activeModal) {
      case "family":
        resolvedCustomModal = (
          <FamilyManagement
            open={true}
            close={closeModalAndRefresh}
            initialUnits={selectedRows}
          />
        );
        break;
      case "inventoryAdjustment":
        if (selectedRows.length > 0) {
          resolvedCustomModal = (
            <AdjustInventory
              open={true}
              close={closeModalAndRefresh}
              initialUnits={selectedRows}
              userId={userId}
              onGridRefresh={onGridRefresh}
            />
          );
        }
        break;
      case "inventoryDetails":
        if (selectedRows.length === 1) {
          resolvedCustomModal = (
            <InventoryDetailsEditable
              open={true}
              onClose={closeModalAndRefresh}
              supplyUnit={selectedRows[0]}
            />
          );
        }
        break;
      case "mergeInventory":
        if (selectedRows.length >= 2) {
          resolvedCustomModal = (
            <MergeInventory
              open={true}
              onClose={closeModalAndRefresh}
              inventoryIds={selectedRows.map((row) => row.supply?.upc)}
            />
          );
        }
        break;
      case "bulkEditInventory":
        if (selectedRows.length >= 2) {
          resolvedCustomModal = (
            <InventoryBulkEdit
              open={true}
              onClose={closeModalAndRefresh}
              inventory={selectedRows}
            />
          );
        }
        break;
    }
  }

  const inventorySubmenu = (
    <Menu
      open={Boolean(inventoryAnchorEl)}
      anchorEl={inventoryAnchorEl}
      onClose={handleInventoryMenuClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        root: {
          onMouseLeave: handleInventoryMenuClose,
        },
      }}
    >
      <MenuItem onClick={() => openContextModal("inventoryAdjustment")}>
        <ListItemIcon>
          <Inventory2Outlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Adjust Inventory</ListItemText>
      </MenuItem>

      <MenuItem
        disabled={selectedRows.length > 1}
        onClick={() => openContextModal("inventoryDetails")}
      >
        <ListItemIcon>
          <SettingsOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Inventory Details</ListItemText>
      </MenuItem>

      <MenuItem
        disabled={selectedRows.length < 2}
        onClick={() => openContextModal("mergeInventory")}
      >
        <ListItemIcon>
          <MergeTypeOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Merge Inventory</ListItemText>
      </MenuItem>

      <MenuItem
        disabled={selectedRows.length < 2}
        onClick={() => openContextModal("bulkEditInventory")}
      >
        <ListItemIcon>
          <EditNoteOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Bulk Edit</ListItemText>
      </MenuItem>
    </Menu>
  );

  return (
    <BaseContextMenu
      open={open}
      event={event}
      closeContextMenu={handleMainMenuClose}
      selectedRows={selectedRows}
      onGridRefresh={onGridRefresh}
      activeCustomModal={resolvedCustomModal}
      extraNodes={inventorySubmenu}
      {...rest}
    >
      <MenuItem onClick={() => openContextModal("family")}>
        <ListItemIcon>
          <ApartmentOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit Families</ListItemText>
      </MenuItem>

      <MenuItem onMouseEnter={handleInventoryMenuOpen}>
        <ListItemIcon>
          <Inventory2Outlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Inventory</ListItemText>
        <ChevronRight fontSize="small" />
      </MenuItem>
    </BaseContextMenu>
  );
};

export default InventoryContextMenu;
